import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

function renderInlineMarkdown(text) {
  if (!text) return '';
  const content = String(text);
  const tokens = /\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\)/g;
  const segments = [];
  let lastIndex = 0;
  let match;
  let keyCounter = 0;

  while ((match = tokens.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push(content.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith('**')) {
      segments.push(<strong key={`md-strong-${keyCounter}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      segments.push(<em key={`md-em-${keyCounter}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      segments.push(<code key={`md-code-${keyCounter}`}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const labelMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (labelMatch) {
        segments.push(
          <a key={`md-link-${keyCounter}`} href={labelMatch[2]} target="_blank" rel="noreferrer">
            {labelMatch[1]}
          </a>
        );
      } else {
        segments.push(token);
      }
    }

    keyCounter += 1;
    lastIndex = tokens.lastIndex;
  }

  if (lastIndex < content.length) {
    segments.push(content.slice(lastIndex));
  }

  if (!segments.length) {
    return content;
  }

  return segments;
}

function expandStructuredBlocks(blocks = [], fallbackText = '') {
  const sourceBlocks = blocks && blocks.length
    ? blocks
    : fallbackText
      ? [{ id: 'fallback', type: 'paragraph', text: fallbackText }]
      : [];

  if (!sourceBlocks.length) return [];

  let syntheticId = 0;
  const nextId = (base) => {
    syntheticId += 1;
    return `${base || 'block'}-${syntheticId}`;
  };

  const expanded = [];

  sourceBlocks.forEach((block, index) => {
    if (!block) {
      return;
    }

    if (block.type && block.type !== 'paragraph') {
      expanded.push(block);
      return;
    }

    const baseId = block.id || `paragraph-${index}`;
    const text = (block.text || '').replace(/\r\n/g, '\n');
    const lines = text.split('\n');
    let listBuffer = [];
    let listStyle = 'bullet';

    const flushList = () => {
      if (!listBuffer.length) return;
      const items = listBuffer.map((item, itemIndex) => ({
        id: `${baseId}-list-item-${itemIndex}`,
        text: item
      }));
      expanded.push({
        ...block,
        id: nextId(`${baseId}-list`),
        type: 'list',
        style: listStyle,
        items
      });
      listBuffer = [];
    };

    lines.forEach((rawLine) => {
      const trimmedLine = rawLine.trim();
      if (!trimmedLine) {
        flushList();
        return;
      }

      const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        flushList();
        const level = headingMatch[1].length;
        expanded.push({
          ...block,
          id: nextId(`${baseId}-heading`),
          type: 'heading',
          level,
          text: headingMatch[2]
        });
        return;
      }

      const numberedMatch = trimmedLine.match(/^\d+[.)]\s+(.*)$/);
      if (numberedMatch) {
        if (listStyle !== 'numbered' && listBuffer.length) {
          flushList();
        }
        listStyle = 'numbered';
        listBuffer.push(numberedMatch[1]);
        return;
      }

      const bulletMatch = trimmedLine.match(/^[-*+]\s+(.*)$/);
      if (bulletMatch) {
        if (listStyle !== 'bullet' && listBuffer.length) {
          flushList();
        }
        listStyle = 'bullet';
        listBuffer.push(bulletMatch[1]);
        return;
      }

      const quoteMatch = trimmedLine.match(/^>\s+(.*)$/);
      if (quoteMatch) {
        flushList();
        expanded.push({
          ...block,
          id: nextId(`${baseId}-quote`),
          type: 'quote',
          text: quoteMatch[1]
        });
        return;
      }

      flushList();
      expanded.push({
        ...block,
        id: nextId(`${baseId}-paragraph`),
        type: 'paragraph',
        text: trimmedLine
      });
    });

    flushList();
  });

  return expanded;
}

function Heading({ text, level = 3 }) {
  const Tag = level >= 1 && level <= 6 ? `h${level}` : 'h3';
  return <Tag className="tc-block-heading">{renderInlineMarkdown(text)}</Tag>;
}

function Paragraph({ text }) {
  return (
    <p className="tc-block-paragraph">
      <span style={{ whiteSpace: 'pre-wrap' }}>{renderInlineMarkdown(text)}</span>
    </p>
  );
}

function ListBlock({ items, style }) {
  if (!items || !items.length) return null;
  const renderItems = () => items.map((it, i) => {
    const content = typeof it === 'string' ? it : it?.text || '';
    return <li key={it?.id || i}>{renderInlineMarkdown(content)}</li>;
  });
  return style === 'numbered' ? (
    <ol className="tc-block-list">{renderItems()}</ol>
  ) : (
    <ul className="tc-block-list">{renderItems()}</ul>
  );
}

function Quote({ text }) {
  return <blockquote className="tc-block-quote">{renderInlineMarkdown(text)}</blockquote>;
}

function Divider() {
  return <hr className="tc-block-divider" />;
}

function ButtonGroup({ buttons }) {
  if (!buttons || !buttons.length) return null;
  return (
    <div className="tc-block-button-group">
      {buttons.map((b, i) => (
        <button
          key={i}
          type="button"
          className="tc-block-cta"
          disabled={!!b.disabled}
          onClick={() => {
            // shallow action: dispatch custom event so parent can handle
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
              const ev = new CustomEvent('agent-block-action', { detail: { action: b.action, label: b.label } });
              window.dispatchEvent(ev);
            }
          }}
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}

export default function StructuredBlocks({ blocks = [], fallbackText = '' }) {
  const preparedBlocks = useMemo(() => expandStructuredBlocks(blocks, fallbackText), [blocks, fallbackText]);

  if (!preparedBlocks.length) return null;

  return (
    <div className="tc-structured-blocks">
      {preparedBlocks.map((b, idx) => {
        if (!b) return null;
        const key = b.id || `block-${idx}`;
        switch (b.type) {
          case 'heading':
            return <Heading key={key} text={b.text || b.raw?.text || ''} level={b.level} />;
          case 'paragraph':
            return <Paragraph key={key} text={b.text || b.raw?.text || ''} />;
          case 'list':
            return <ListBlock key={key} items={b.items} style={b.style} />;
          case 'quote':
            return <Quote key={key} text={b.text || ''} />;
          case 'divider':
            return <Divider key={key} />;
          case 'button_group':
            return <ButtonGroup key={key} buttons={b.buttons} />;
          default:
            console.warn('Unknown structured block type:', b.type, b);
            return <Paragraph key={key} text={b.text || fallbackText || ''} />;
        }
      })}
    </div>
  );
}

StructuredBlocks.propTypes = {
  blocks: PropTypes.array,
  fallbackText: PropTypes.string
};
